/**
 * Landing Page for Customer (unified app)
 * Routes: /
 */

import { useTranslation } from 'react-i18next'
import {
  QrCode,
  CheckCircle,
  Shield,
  FileCheck,
  CreditCard,
  Building2,
  Zap,
} from 'lucide-react'
import Navigation from '../../components/customer/Navigation'
import Footer from '../../components/customer/Footer'
import CompanyRegistrationSection from '../../components/customer/CompanyRegistrationSection'
import ContactForm from '../../components/customer/ContactForm'
import { usePaymentModalStore } from '../../store/paymentModalStore'

export default function LandingPage() {
  const { t } = useTranslation('portal')
  const openPaymentModal = usePaymentModalStore((s) => s.openModal)

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <Navigation />

      <section id="home" className="pt-32 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-5xl font-bold text-slate-900 mb-6">
                {t('landing.hero.title')}
              </h2>
              <p className="text-xl text-slate-600 mb-8">
                {t('landing.hero.subtitle')}
              </p>
              <div className="flex gap-4 flex-wrap justify-center md:justify-start">
                <button
                  onClick={() => openPaymentModal()}
                  className="px-8 py-4 text-lg font-semibold text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-all transform hover:scale-105 shadow-lg inline-flex items-center justify-center gap-2"
                >
                  <QrCode size={24} />
                  {t('landing.hero.cta')}
                </button>
                <a
                  href="#about"
                  className="px-8 py-4 text-lg font-semibold text-blue-600 bg-white border-2 border-blue-600 rounded-lg hover:bg-blue-50 transition-all inline-flex items-center justify-center"
                >
                  {t('landing.hero.learnMore')}
                </a>
              </div>
            </div>
            <div className="flex justify-center">
              <div className="bg-white p-8 rounded-2xl shadow-2xl">
                <QrCode size={200} className="text-blue-600" strokeWidth={1.5} />
              </div>
            </div>
          </div>
        </div>
      </section>

      <section id="pay-invoice" className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-center text-slate-900 mb-12">
            {t('landing.about.howItWorks.title')}
          </h2>
          <div className="grid md:grid-cols-4 gap-8">
            <div className="text-center p-6 bg-slate-50 rounded-xl hover:shadow-lg transition-shadow relative">
              <div className="absolute -top-4 right-4 w-10 h-10 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-lg">1</div>
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <QrCode className="text-blue-600" size={32} />
              </div>
              <p className="text-slate-700">{t('landing.about.howItWorks.step1')}</p>
            </div>
            <div className="text-center p-6 bg-slate-50 rounded-xl hover:shadow-lg transition-shadow relative">
              <div className="absolute -top-4 right-4 w-10 h-10 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-lg">2</div>
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="text-green-600" size={32} />
              </div>
              <p className="text-slate-700">{t('landing.about.howItWorks.step2')}</p>
            </div>
            <div className="text-center p-6 bg-slate-50 rounded-xl hover:shadow-lg transition-shadow relative">
              <div className="absolute -top-4 right-4 w-10 h-10 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-lg">3</div>
              <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CreditCard className="text-purple-600" size={32} />
              </div>
              <p className="text-slate-700">{t('landing.about.howItWorks.step3')}</p>
            </div>
            <div className="text-center p-6 bg-slate-50 rounded-xl hover:shadow-lg transition-shadow relative">
              <div className="absolute -top-4 right-4 w-10 h-10 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-lg">4</div>
              <div className="w-16 h-16 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Building2 className="text-indigo-600" size={32} />
              </div>
              <p className="text-slate-700">{t('landing.about.howItWorks.step4')}</p>
            </div>
          </div>
        </div>
      </section>

      <section id="for-companies">
        <CompanyRegistrationSection />
      </section>

      <section id="about" className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-white to-slate-50">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-4xl font-bold text-center text-slate-900 mb-6">
            {t('landing.about.title')}
          </h2>
          <p className="text-lg text-slate-600 text-center max-w-3xl mx-auto mb-16 leading-relaxed">
            {t('landing.about.intro')}
          </p>
          <div className="grid md:grid-cols-3 gap-8 mb-20">
            <div className="bg-white p-8 rounded-xl shadow-lg hover:shadow-xl transition-all hover:-translate-y-1 border border-slate-100 flex flex-col items-center md:items-start text-center md:text-left">
              <div className="w-16 h-16 bg-blue-100 rounded-lg flex items-center justify-center mb-6 shrink-0">
                <Shield className="text-blue-600" size={32} />
              </div>
              <h3 className="text-2xl font-bold text-slate-900 mb-4">{t('landing.about.technology.title')}</h3>
              <p className="text-slate-600 leading-relaxed">{t('landing.about.technology.description')}</p>
            </div>
            <div className="bg-white p-8 rounded-xl shadow-lg hover:shadow-xl transition-all hover:-translate-y-1 border border-slate-100 flex flex-col items-center md:items-start text-center md:text-left">
              <div className="w-16 h-16 bg-green-100 rounded-lg flex items-center justify-center mb-6 shrink-0">
                <FileCheck className="text-green-600" size={32} />
              </div>
              <h3 className="text-2xl font-bold text-slate-900 mb-4">{t('landing.about.compliance.title')}</h3>
              <p className="text-slate-600 leading-relaxed">{t('landing.about.compliance.description')}</p>
            </div>
            <div className="bg-white p-8 rounded-xl shadow-lg hover:shadow-xl transition-all hover:-translate-y-1 border border-slate-100 flex flex-col items-center md:items-start text-center md:text-left">
              <div className="w-16 h-16 bg-purple-100 rounded-lg flex items-center justify-center mb-6 shrink-0">
                <Zap className="text-purple-600" size={32} />
              </div>
              <h3 className="text-2xl font-bold text-slate-900 mb-4">{t('landing.howItWorks.title')}</h3>
              <p className="text-slate-600 leading-relaxed">{t('landing.howItWorks.step1Desc')}</p>
            </div>
          </div>
        </div>
      </section>

      <section id="contact" className="py-20 px-4 sm:px-6 lg:px-8 bg-slate-50">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-4xl font-bold text-center text-slate-900 mb-6">
            {t('landing.contact.title')}
          </h2>
          <p className="text-lg text-slate-600 text-center max-w-2xl mx-auto mb-16">
            {t('landing.contact.subtitle')}
          </p>
          <div className="grid md:grid-cols-3 gap-12">
            <div className="md:col-span-2">
              <ContactForm />
            </div>
            <div className="bg-white p-8 rounded-xl shadow-lg h-fit">
              <h3 className="text-2xl font-bold text-slate-900 mb-6">
                {t('landing.contact.info.title')}
              </h3>
              <div className="space-y-6">
                <div>
                  <p className="text-sm font-semibold text-blue-600 uppercase tracking-wide mb-2">{t('landing.contact.info.email')}</p>
                  <a href="mailto:sasa.drekovic@gmail.com" className="text-slate-700 hover:text-blue-600 transition-colors break-all">
                    sasa.drekovic@gmail.com
                  </a>
                </div>
                <div>
                  <p className="text-sm font-semibold text-blue-600 uppercase tracking-wide mb-2">{t('landing.contact.info.phone')}</p>
                  <a href="tel:+38765830040" className="text-slate-700 hover:text-blue-600 transition-colors">
                    +387 65 830 040
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  )
}
